import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  forwardRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MULTI_SELECT_GROUPED_OVERLAY_PANEL_CLASS } from '../../utils/dom-events/dropdown-overlay.util';
import { DropdownOverlayBase } from '../../utils/dropdown-overlay-base/dropdown-overlay-base';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';

export interface IGroup {
  name: string;
  id: number;
  options: IOption[];
}

export interface IOption {
  name: string;
  id: number;
  isChecked?: boolean;
  icon?: string;
  count?: number;
}

export type IMultiSelectGroupedSettings = IMultiSelectSettings

@Component({
  selector: 'sgt-multi-select-grouped',
  templateUrl: './multi-select-grouped.component.html',
  styleUrls: ['./multi-select-grouped.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectGroupedComponent),
      multi: true
    }
  ]
})
export class MultiSelectGroupedComponent
  extends DropdownOverlayBase<number[]>
  implements OnInit, OnChanges {
  @Input() options: IGroup[] = [];
  @Input() settings: IMultiSelectGroupedSettings;
  @Input() preserveCase = false;

  @Output() search = new EventEmitter<boolean>();
  @Output() isFirstOpened = new EventEmitter<boolean>();
  @Output() hasOpened = new EventEmitter<boolean>();

  @ViewChild('inputRef') inputRef: ElementRef<HTMLElement>;
  @ViewChild('optionsList') optionsList: ElementRef<HTMLUListElement>;

  private isFirstOpen = true;

  selectedTotalOptions = 0;

  get semDados(): boolean {
    return !!this.settings?.isLoading || !this.totalOptions;
  }

  get totalVisibleOptions(): number {
    return this.filteredOptions.reduce(
      (total, grupo) => total + grupo.options.length,
      0
    );
  }

  get selectedVisibleOptions(): number {
    return this.filteredOptions.reduce(
      (total, grupo) => total + grupo.options.filter((o) => o.isChecked).length,
      0
    );
  }

  get selectAllLabel(): string {
    return this.searchText
      ? 'Selecionar todos os resultados'
      : 'Selecionar todos';
  }

  get todasVisiveisSelecionadas(): boolean {
    return (
      this.selectedVisibleOptions > 0 &&
      this.selectedVisibleOptions === this.totalVisibleOptions
    );
  }

  get selecaoVisivelParcial(): boolean {
    return (
      this.selectedVisibleOptions > 0 &&
      this.selectedVisibleOptions < this.totalVisibleOptions
    );
  }

  customPlaceholder = '';
  searchText = '';
  totalOptions = 0;
  filteredOptions: IGroup[] = [];
  protected readonly overlayPanelClass = MULTI_SELECT_GROUPED_OVERLAY_PANEL_CLASS;

  protected get triggerElement(): HTMLElement {
    return this.inputRef.nativeElement;
  }

  constructor(
    elementRef: ElementRef<HTMLElement>,
    ngZone: NgZone,
    changeDetectorRef: ChangeDetectorRef,
    overlay: Overlay
  ) {
    super(elementRef, ngZone, changeDetectorRef, overlay);
  }

  ngOnInit(): void {
    if (this.options?.length) {
      this.filteredOptions = JSON.parse(JSON.stringify(this.options));
      this.getTotalOptions();
    }
  }

  ngOnChanges(): void {
    this.setDisabledPorSettings(!!this.settings?.isDisabled);

    if (this.settings?.isLoading) {
      return;
    }

    if (this.options?.length) {
      this.onSearch();
      this.totalSelectedOptions();
    }

    this.changePlaceholder();
  }

  open() {
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.prepararOverlay();
    } else {
      this.closeDropdown();
    }

    if (this.isFirstOpen) {
      this.isFirstOpened.emit(this.isFirstOpen);
      this.isFirstOpen = false;
    }

    this.hasOpened.emit(this.isOpen);
  }

  onSearch(): void {
    const text = this.searchText.toLowerCase().trim();

    if (!text) {
      this.filteredOptions = JSON.parse(JSON.stringify(this.options));
      this.getTotalOptions();
      return;
    }

    this.filteredOptions = this.options
      .map((group) => {
        const filteredOptions = group.options.filter((option) =>
          option.name.toLowerCase().includes(text)
        );

        return {
          ...group,
          options: filteredOptions
        };
      })
      .filter((group) => group.options.length > 0);

    this.getTotalOptions();
  }

  restoreOriginalOptions(): void {
    this.filteredOptions = JSON.parse(JSON.stringify(this.options));
    this.getTotalOptions();
  }

  clearSearchFilter() {
    this.searchText = '';
    this.restoreOriginalOptions();
    this.changePlaceholder();
  }

  clearSearchInput(event?: Event) {
    event?.stopPropagation();
    this.searchText = '';
    this.restoreOriginalOptions();
  }

  getTotalOptions(): void {
    this.totalOptions = this.options.reduce((count: number, group: IGroup) => count + group.options.length, 0);
  }

  selectOption(option: IOption, event?: Event) {
    const isChecked =
      (event?.target as HTMLInputElement)?.checked ?? !option.isChecked;
    option.isChecked = isChecked;

    this.syncWithOriginalOptions(option.id, isChecked);

    this.totalSelectedOptions();
    this.changePlaceholder();
    this.emitSelectedValues();
    this.markAsTouched();
  }

  private syncWithOriginalOptions(optionId: number, isChecked: boolean): void {
    this.options.forEach((group) => {
      group.options.forEach((option) => {
        if (option.id === optionId) {
          option.isChecked = isChecked;
        }
      });
    });
  }

  totalSelectedOptions(): void {
    this.selectedTotalOptions = this.options.reduce((count, group) => count + group.options.filter((option) => option.isChecked).length, 0);
  }

  selectAllOptions(event: Event): void {
    event.stopPropagation();

    const targetGroups = this.searchText ? this.filteredOptions : this.options;
    const allSelected = this.areAllOptionsSelected(targetGroups);
    const newChecked = !allSelected;

    this.setOptionsChecked(targetGroups, newChecked);

    if (!this.searchText) {
      this.setOptionsChecked(this.filteredOptions, newChecked);
    }

    this.totalSelectedOptions();
    this.changePlaceholder();
    this.emitSelectedValues();
    this.markAsTouched();
  }

  private areAllOptionsSelected(groups: IGroup[]): boolean {
    return groups.every((group) =>
      group.options.every((option) => option.isChecked)
    );
  }

  private setOptionsChecked(groups: IGroup[], checked: boolean): void {
    groups.forEach((group) =>
      group.options.forEach((option) => {
        option.isChecked = checked;
        if (this.searchText) {
          this.syncWithOriginalOptions(option.id, checked);
        }
      })
    );
  }

  close() {
    this.resetOriginalOptions();
    this.resetFilteredOptions();
    this.selectedTotalOptions = 0;
    this.customPlaceholder = '';
    this.clearSearchFilter();
    this.closeDropdown();
    this.emitChange([]);
    this.markAsTouched();
    this.search.emit(false);
  }

  private resetOriginalOptions() {
    this.options.forEach((group: IGroup) =>
      group.options.forEach((option) => (option.isChecked = false))
    );
  }

  private resetFilteredOptions() {
    this.filteredOptions.forEach((group: IGroup) =>
      group.options.forEach((option) => (option.isChecked = false))
    );
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.encerrarObservacaoDeScroll();
    if (this.optionsList?.nativeElement) {
      this.optionsList.nativeElement.scrollTop = 0;
    }
  }

  protected aoFecharPorInteracaoExterna(): void {
    this.checkIfEmitAfterClosed();
  }

  private checkIfEmitAfterClosed(): void {
    this.closeDropdown();
    this.clearSearchFilter();
    this.markAsTouched();

    if (this.settings?.emitAfterClosed) {
      const hasSelectedOptions = this.selectedTotalOptions > 0;
      this.search.emit(hasSelectedOptions);
    }
  }

  searchOptions() {
    this.search.emit(true);
    this.closeDropdown();
    this.clearSearchFilter();
    this.markAsTouched();
  }

  changePlaceholder(): void {
    this.customPlaceholder = this.selectedTotalOptions
      ? this.settings?.plural ?? 'opções selecionadas'
      : this.settings?.placeholder;
  }

  protected updateValue(value: number[] | null): void {
    if (!this.options) {
      return;
    }

    const selectedIds = value || [];

    // Atualiza as opções baseadas no valor recebido
    this.options.forEach((group) => {
      group.options.forEach((option) => {
        option.isChecked = selectedIds.includes(option.id);
      });
    });

    // Atualiza as opções filtradas também
    if (this.filteredOptions) {
      this.filteredOptions.forEach((group) => {
        group.options.forEach((option) => {
          option.isChecked = selectedIds.includes(option.id);
        });
      });
    }

    this.totalSelectedOptions();
    this.changePlaceholder();
  }

  /**
   * Emite o valor atual (IDs das opções selecionadas) para o FormControl
   */
  private emitSelectedValues(): void {
    const selectedIds = this.getSelectedOptionIds();
    this.emitChange(selectedIds);
  }

  /**
   * Retorna os IDs das opções atualmente selecionadas
   */
  private getSelectedOptionIds(): number[] {
    const selectedIds: number[] = [];
    if (this.options) {
      this.options.forEach((group) => {
        group.options.forEach((option) => {
          if (option.isChecked) {
            selectedIds.push(option.id);
          }
        });
      });
    }
    return selectedIds;
  }
}
